import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  FileText, Download, Mail, Receipt, Users as UsersIcon, ShieldCheck, Calculator, TrendingUp,
} from 'lucide-react';
import { DateRangePresets, fmtLocal } from '../components/reports/DateRangePresets';
import api, { formatApiError } from '../lib/api';
import { toast } from 'sonner';

const fmtR = (v) => `R ${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDt = (iso) => iso ? new Date(iso).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const today = () => fmtLocal(new Date());
const monthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return fmtLocal(d);
};

export const Billing = () => {
  return (
    <Layout title="Billing & Statements" subtitle="Client & Contractor statement generation">
      <div className="space-y-6 animate-fade-in" data-testid="billing-page">
        <Tabs defaultValue="client">
          <TabsList>
            <TabsTrigger value="client" className="gap-2" data-testid="tab-client-statements">
              <Receipt className="w-4 h-4" /> Client Statements
            </TabsTrigger>
            <TabsTrigger value="contractor" className="gap-2" data-testid="tab-contractor-statements">
              <ShieldCheck className="w-4 h-4" /> Contractor Statements
            </TabsTrigger>
          </TabsList>

          <TabsContent value="client" className="mt-4">
            <ClientStatementPanel />
          </TabsContent>
          <TabsContent value="contractor" className="mt-4">
            <ContractorStatementPanel />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};


// ============= CLIENT STATEMENT =============
const ClientStatementPanel = () => {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [start, setStart] = useState(monthAgo());
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [recipients, setRecipients] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get('/customers').then((r) => {
      setCustomers(r.data || []);
      if (r.data?.length && !customerId) setCustomerId(r.data[0].id);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const customer = customers.find((c) => c.id === customerId);

  const fetchStatement = useCallback(async () => {
    if (!customerId) return;
    setLoading(true); setData(null);
    try {
      const r = await api.get(`/billing/customer/${customerId}/statement`, { params: { start, end } });
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [customerId, start, end]);

  const handleDownload = async () => {
    if (!customerId) return;
    try {
      const r = await api.get(`/billing/customer/${customerId}/statement/pdf`, {
        params: { start, end }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `client-statement-${customer?.code || customerId.slice(0,6)}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  const openSend = () => {
    setRecipients(customer?.email || '');
    setMessage('');
    setSendOpen(true);
  };

  const handleSend = async () => {
    const list = recipients.split(/[,\s]+/).filter(Boolean);
    if (!list.length) { toast.error('Enter at least one email'); return; }
    setSending(true);
    try {
      const r = await api.post(`/billing/customer/${customerId}/statement/send`, {
        start, end, recipients: list, message: message || null,
      });
      toast.success(`Statement emailed to ${r.data.recipients.join(', ')}`);
      setSendOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <Card className="bg-white border-slate-200" data-testid="client-statement-panel">
      <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-4">
        <CardTitle className="font-heading text-lg text-[#0D5B69]">Generate Client Statement</CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Statements include all closed and cancelled incidents in the period. Cancelled incidents within the customer's no-bill window are flagged.
        </p>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        <DateRangePresets
          start={start} end={end} setStart={setStart} setEnd={setEnd}
          testidPrefix="client-preset"
        />
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div className="md:col-span-2 space-y-1">
            <Label className="text-xs uppercase tracking-wide text-slate-600">Customer</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="client-customer-select"><SelectValue placeholder="Pick a customer" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-slate-600">From</Label>
            <Input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)} data-testid="client-start-date" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-slate-600">To</Label>
            <Input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} data-testid="client-end-date" />
          </div>
          <Button onClick={fetchStatement} disabled={loading || !customerId} className="bg-[#0D5B69] hover:bg-[#094450] text-white gap-1 font-semibold" data-testid="generate-client-btn">
            <Calculator className="w-4 h-4" /> {loading ? 'Computing…' : 'Generate'}
          </Button>
        </div>

        {data && (
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatMini label="Billable" value={data.billable_count} />
              <StatMini label="No-Bill" value={data.no_bill_count} tone="muted" />
              <StatMini label="Subtotal (ex VAT)" value={fmtR(data.subtotal)} />
              <StatMini label="Total (incl VAT)" value={fmtR(data.total)} tone="accent" />
            </div>

            <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="client-statement-results">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="text-xs">Incident</TableHead>
                    <TableHead className="text-xs">Contractor / Service</TableHead>
                    <TableHead className="text-xs">Duration</TableHead>
                    <TableHead className="text-xs text-right">Rate</TableHead>
                    <TableHead className="text-xs text-right">Amount (ex VAT)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.summaries.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">No incidents in this period.</TableCell></TableRow>
                  ) : data.summaries.flatMap((s, si) => {
                    if (s.no_bill) {
                      return [
                        <TableRow key={`${si}-nobill`} className="bg-slate-50/50">
                          <TableCell className="font-mono text-sm">{s.incident_no}</TableCell>
                          <TableCell colSpan={3} className="text-xs text-slate-500 italic">{s.no_bill_reason}</TableCell>
                          <TableCell className="text-right">
                            <Badge variant="outline" className="text-[10px]">NO BILL</Badge>
                          </TableCell>
                        </TableRow>,
                      ];
                    }
                    return s.segments.map((seg, segi) => (
                      <TableRow key={`${si}-${segi}`}>
                        <TableCell className="font-mono text-sm text-[#0D5B69]">{segi === 0 ? s.incident_no : ''}</TableCell>
                        <TableCell className="text-sm">
                          <p className="font-medium text-slate-900">{seg.contractor_name}</p>
                          <p className="text-xs text-slate-500">{seg.service_name} · {fmtDt(seg.started_at)}</p>
                        </TableCell>
                        <TableCell className="text-sm">
                          {seg.billing_type === 'fixed' ? <Badge variant="outline">Fixed</Badge> : `${seg.duration_minutes.toFixed(1)} min`}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">{fmtR(seg.client_rate)}</TableCell>
                        <TableCell className="text-right font-mono text-sm font-semibold">{fmtR(seg.client_amount)}</TableCell>
                      </TableRow>
                    ));
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleDownload} className="gap-1" data-testid="download-client-pdf">
                <Download className="w-4 h-4" /> Download PDF
              </Button>
              <Button onClick={openSend} className="bg-[#80C030] hover:bg-[#6FA528] text-white gap-1 font-semibold" data-testid="send-client-btn">
                <Mail className="w-4 h-4" /> Email to Accounts
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0D5B69]">Email Client Statement</DialogTitle>
            <DialogDescription>{customer?.company_name} — PDF will be attached.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Recipients</Label>
              <Input value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="accounts@csgconnect.co.za, ar@discovery.co.za" data-testid="client-recipients-input" />
              <p className="text-[11px] text-slate-400">Separate multiple emails with commas.</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Message (optional)</Label>
              <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="e.g. As discussed yesterday — see attached for Feb statement" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button>
            <Button onClick={handleSend} disabled={sending} className="bg-[#80C030] hover:bg-[#6FA528] text-white gap-1 font-semibold" data-testid="confirm-send-client-btn">
              <Mail className="w-4 h-4" /> {sending ? 'Sending…' : 'Send Statement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};


// ============= CONTRACTOR STATEMENT =============
const ContractorStatementPanel = () => {
  const [contractors, setContractors] = useState([]);
  const [contractorId, setContractorId] = useState('');
  const [start, setStart] = useState(monthAgo());
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [recipients, setRecipients] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get('/contractors').then((r) => {
      setContractors(r.data || []);
      if (r.data?.length && !contractorId) setContractorId(r.data[0].id);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const contractor = contractors.find((c) => c.id === contractorId);

  const fetchStatement = useCallback(async () => {
    if (!contractorId) return;
    setLoading(true); setData(null);
    try {
      const r = await api.get(`/billing/contractor/${contractorId}/statement`, { params: { start, end } });
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [contractorId, start, end]);

  const handleDownload = async () => {
    if (!contractorId) return;
    try {
      const r = await api.get(`/billing/contractor/${contractorId}/statement/pdf`, {
        params: { start, end }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `contractor-statement-${contractor?.code || contractorId.slice(0,6)}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  const openSend = () => {
    setRecipients(contractor?.email || '');
    setMessage('');
    setSendOpen(true);
  };

  const handleSend = async () => {
    const list = recipients.split(/[,\s]+/).filter(Boolean);
    if (!list.length) { toast.error('Enter at least one email'); return; }
    setSending(true);
    try {
      const r = await api.post(`/billing/contractor/${contractorId}/statement/send`, {
        start, end, recipients: list, message: message || null,
      });
      toast.success(`Recipient statement emailed to ${r.data.recipients.join(', ')}`);
      setSendOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <Card className="bg-white border-slate-200" data-testid="contractor-statement-panel">
      <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-4">
        <CardTitle className="font-heading text-lg text-[#0D5B69]">Generate Contractor Recipient Statement</CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Recipient statements list every assignment the contractor attended. The contractor uses this to prepare their invoice to CSG.
        </p>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        <DateRangePresets
          start={start} end={end} setStart={setStart} setEnd={setEnd}
          testidPrefix="contractor-preset"
        />
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div className="md:col-span-2 space-y-1">
            <Label className="text-xs uppercase tracking-wide text-slate-600">Contractor</Label>
            <Select value={contractorId} onValueChange={setContractorId}>
              <SelectTrigger data-testid="contractor-select"><SelectValue placeholder="Pick a contractor" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {contractors.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-slate-600">From</Label>
            <Input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)} data-testid="contractor-start-date" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wide text-slate-600">To</Label>
            <Input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} data-testid="contractor-end-date" />
          </div>
          <Button onClick={fetchStatement} disabled={loading || !contractorId} className="bg-[#0D5B69] hover:bg-[#094450] text-white gap-1 font-semibold" data-testid="generate-contractor-btn">
            <Calculator className="w-4 h-4" /> {loading ? 'Computing…' : 'Generate'}
          </Button>
        </div>

        {data && (
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <StatMini label="Assignments" value={data.rows.length} />
              <StatMini label="Total Payable (excl VAT)" value={fmtR(data.total)} tone="accent" />
            </div>
            <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="contractor-statement-results">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="text-xs">Incident</TableHead>
                    <TableHead className="text-xs">Customer · Service</TableHead>
                    <TableHead className="text-xs">When</TableHead>
                    <TableHead className="text-xs">Duration</TableHead>
                    <TableHead className="text-xs text-right">Rate</TableHead>
                    <TableHead className="text-xs text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.rows.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No assignments for this contractor in the period.</TableCell></TableRow>
                  ) : data.rows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-sm text-[#0D5B69]">{r.incident_no}</TableCell>
                      <TableCell className="text-sm">
                        <p className="font-medium text-slate-900">{r.customer_name}</p>
                        <p className="text-xs text-slate-500">{r.service_name}</p>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">{fmtDt(r.started_at)}</TableCell>
                      <TableCell className="text-sm">{r.duration_minutes.toFixed(1)} min</TableCell>
                      <TableCell className="text-right font-mono text-sm">{fmtR(r.rate)}</TableCell>
                      <TableCell className="text-right font-mono text-sm font-semibold">{fmtR(r.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleDownload} className="gap-1" data-testid="download-contractor-pdf">
                <Download className="w-4 h-4" /> Download PDF
              </Button>
              <Button onClick={openSend} className="bg-[#80C030] hover:bg-[#6FA528] text-white gap-1 font-semibold" data-testid="send-contractor-btn">
                <Mail className="w-4 h-4" /> Email to Contractor
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0D5B69]">Email Recipient Statement</DialogTitle>
            <DialogDescription>{contractor?.company_name} — PDF will be attached.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Recipients</Label>
              <Input value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="ops@vdbsecurity.co.za" data-testid="contractor-recipients-input" />
              <p className="text-[11px] text-slate-400">Separate multiple emails with commas.</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Message (optional)</Label>
              <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Please use this statement to invoice CSG for the period above." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button>
            <Button onClick={handleSend} disabled={sending} className="bg-[#80C030] hover:bg-[#6FA528] text-white gap-1 font-semibold" data-testid="confirm-send-contractor-btn">
              <Mail className="w-4 h-4" /> {sending ? 'Sending…' : 'Send Statement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};


const StatMini = ({ label, value, tone }) => (
  <Card className={`bg-white border-slate-200 ${tone === 'accent' ? 'border-[#80C030]/40' : ''}`}>
    <CardContent className="p-3">
      <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`text-lg font-bold font-heading mt-1 ${tone === 'accent' ? 'text-[#80C030]' : tone === 'muted' ? 'text-slate-400' : 'text-[#0D5B69]'}`}>{value}</p>
    </CardContent>
  </Card>
);
